import { Controller } from "@hotwired/stimulus";

// Income and expense categories must stay in sync with FinancialTransaction::INCOME_CATEGORIES
// and FinancialTransaction::EXPENSE_CATEGORIES in app/models/financial_transaction.rb
const INCOME_CATEGORIES = [
  "Livestock Sales",
  "Egg Sales",
  "Milk Sales",
  "Crop Sales",
  "Seedling Sales",
  "Manure / Compost Sales",
  "Processing Income",
  "Service Income",
  "Grants & Subsidies",
  "Insurance Payout",
  "Other Income",
];

const EXPENSE_CATEGORIES = [
  "Livestock Purchase",
  "Animal Feed",
  "Veterinary & Medicine",
  "Seeds & Seedlings",
  "Fertiliser & Chemicals",
  "Labour (Casual)",
  "Labour (Permanent)",
  "Equipment Purchase",
  "Equipment Repair",
  "Fuel & Transport",
  "Utilities",
  "Land & Rent",
  "Packaging & Storage",
  "Marketing & Sales",
  "Administrative",
  "Loan Repayment",
  "Other Expense",
];

export default class extends Controller {
  static targets = [
    "scope",
    "farm",
    "department",
    "departmentField",
    "transactionType",
    "category",
  ];

  connect() {
    this.syncScope();
    this.syncCategories();

    if (
      this.hasFarmTarget &&
      this.hasDepartmentTarget &&
      this.farmTarget.value
    ) {
      this.loadDepartments(this.farmTarget.value, this.departmentTarget.value);
    }
  }

  scopeChanged() {
    this.syncScope();
  }

  typeChanged() {
    this.syncCategories();
  }

  farmChanged() {
    if (!this.hasDepartmentTarget) return;

    const farmId = this.farmTarget.value;
    if (farmId) {
      this.loadDepartments(farmId, this.departmentTarget.value);
    } else {
      this.clearDepartments();
    }
  }

  syncScope() {
    if (
      !this.hasScopeTarget ||
      !this.hasDepartmentTarget ||
      !this.hasDepartmentFieldTarget
    )
      return;

    const isFarmWide = this.scopeTarget.value === "farm_wide";
    this.departmentFieldTarget.classList.toggle("hidden", isFarmWide);
    this.departmentTarget.disabled = isFarmWide;

    if (isFarmWide) {
      this.departmentTarget.value = "";
    }
  }

  syncCategories() {
    if (!this.hasTransactionTypeTarget || !this.hasCategoryTarget) return;

    const type = this.transactionTypeTarget.value;
    const categories =
      type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
    const select = this.categoryTarget;
    const current = select.value;

    select.innerHTML = '<option value="">Select category (optional)</option>';
    categories.forEach((cat) => {
      const option = document.createElement("option");
      option.value = cat;
      option.textContent = cat;
      if (cat === current) option.selected = true;
      select.appendChild(option);
    });
  }

  async loadDepartments(farmId, selectedDepartmentId) {
    try {
      const response = await fetch(`/farms/${farmId}/departments`, {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) return;

      const departments = await response.json();
      const select = this.departmentTarget;

      const keepValue =
        selectedDepartmentId &&
        departments.some((d) => String(d.id) === String(selectedDepartmentId));

      select.innerHTML = '<option value="">Select department</option>';
      departments.forEach((dept) => {
        const option = document.createElement("option");
        option.value = dept.id;
        option.textContent = dept.name;
        if (keepValue && String(dept.id) === String(selectedDepartmentId)) {
          option.selected = true;
        }
        select.appendChild(option);
      });
    } catch (_err) {
      // Keep current options on network errors
    }
  }

  clearDepartments() {
    if (!this.hasDepartmentTarget) return;

    this.departmentTarget.innerHTML =
      '<option value="">Select department</option>';
    this.departmentTarget.value = "";
  }
}
